import type { Point, Tile } from '../../hex/types';
import { hexRadius } from '../../hex/hexRadius';
import type { HexNeighbor } from './hexRenderTypes';
import { getHexBounds } from './hexRenderUtils';
import { clipHex } from '../common/clipHex';
import { edgeDirs, edgeIndexToward } from '../common/geometry';
import { drawFoamAlongEdge } from '../common/foam';
import { hatch, stipple } from '../common/hatching';
import { fnv1a } from '../common/fnv1a';
import { mulberry32 } from '../common/mulberry32';
import { tileSeed } from '../common/tileSeed';

export type Neighbor = HexNeighbor;

type DrawArgs = {
    ctx: CanvasRenderingContext2D;
    tile: Tile;
    center: Point;
    verts: Point[];
    zoom: number;
    neighbors?: Neighbor[];
};

function isWaterLike(tile: Tile): boolean {
    return tile.biome.type === 'water';
}

export function drawBiomeOverlay(args: DrawArgs) {
    const biome = args.tile.biome.type;

    if (biome === 'mountains') return drawMountains(args);
    if (biome === 'forest') return drawForest(args);
    if (biome === 'water') return drawWater(args);
    if (biome === 'plains') return drawPlains(args);
    if (biome === 'hills') return drawHills(args);
    if (biome === 'wetlands') return drawWetlands(args);
    if (biome === 'coast') return drawCoast(args);
    return undefined;
}

function drawMountains({ ctx, tile, center, verts, zoom }: DrawArgs) {
    const r = hexRadius(center, verts);
    const rng = mulberry32(tileSeed(tile, fnv1a('mountains')));
    const bounds = getHexBounds(verts);

    clipHex(ctx, verts, () => {
        hatch(ctx, bounds, -0.9, (6.5 + rng() * 3.0) / zoom, 0.08, zoom);

        const peaks = 2 + Math.floor(rng() * 3);
        const baseY = center.y + r * 0.28;
        const minX = center.x - r * 0.72;
        const maxX = center.x + r * 0.72;

        ctx.lineWidth = 1.05 / zoom;
        ctx.strokeStyle = 'rgba(0,0,0,0.42)';

        for (let i = 0; i < peaks; i += 1) {
            const cx = minX + (i + 0.5) * ((maxX - minX) / peaks) + (rng() - 0.5) * r * 0.14;
            const height = r * (0.58 + rng() * 0.28);
            const halfW = r * (0.22 + rng() * 0.12);

            ctx.beginPath();
            ctx.moveTo(cx - halfW, baseY);
            ctx.lineTo(cx, baseY - height);
            ctx.lineTo(cx + halfW, baseY);
            ctx.closePath();
            ctx.stroke();

            ctx.save();
            ctx.globalCompositeOperation = 'screen';
            ctx.fillStyle = 'rgba(255,255,255,0.10)';
            ctx.beginPath();
            ctx.moveTo(cx - halfW * 0.98, baseY);
            ctx.lineTo(cx, baseY - height);
            ctx.lineTo(cx - halfW * 0.1, baseY);
            ctx.closePath();
            ctx.fill();
            ctx.restore();

            ctx.save();
            ctx.beginPath();
            ctx.moveTo(cx, baseY - height);
            ctx.lineTo(cx + halfW, baseY);
            ctx.lineTo(cx, baseY);
            ctx.closePath();
            ctx.clip();
            hatch(ctx, bounds, -0.9, (5.0 + rng() * 2.5) / zoom, 0.12, zoom);
            ctx.restore();

            if (rng() > 0.35) {
                const capH = height * (0.17 + rng() * 0.1);
                const capW = halfW * (0.34 + rng() * 0.12);
                ctx.save();
                ctx.globalCompositeOperation = 'screen';
                ctx.fillStyle = 'rgba(255,255,255,0.22)';
                ctx.beginPath();
                ctx.moveTo(cx - capW, baseY - height + capH);
                ctx.lineTo(cx, baseY - height);
                ctx.lineTo(cx + capW, baseY - height + capH);
                ctx.closePath();
                ctx.fill();
                ctx.restore();
            }

            ctx.beginPath();
            ctx.moveTo(cx, baseY - height);
            ctx.lineTo(cx - halfW * 0.35, baseY - height * 0.55);
            ctx.strokeStyle = 'rgba(0,0,0,0.28)';
            ctx.stroke();
            ctx.strokeStyle = 'rgba(0,0,0,0.42)';
        }

        stipple(ctx, rng, bounds, 8 + Math.floor(rng() * 10), 0.07, zoom);
    });
}

function drawForest({ ctx, tile, center, verts, zoom }: DrawArgs) {
    const r = hexRadius(center, verts);
    const rng = mulberry32(tileSeed(tile, fnv1a('forest')));
    const bounds = getHexBounds(verts);

    clipHex(ctx, verts, () => {
        hatch(ctx, bounds, -0.95, (7.0 + rng() * 3.0) / zoom, 0.06, zoom);

        const count = 7 + Math.floor(rng() * 8);
        ctx.lineWidth = 1.0 / zoom;
        ctx.strokeStyle = 'rgba(0,0,0,0.36)';

        for (let i = 0; i < count; i += 1) {
            const x = bounds.left + (bounds.right - bounds.left) * (0.16 + rng() * 0.68);
            const y = bounds.top + (bounds.bottom - bounds.top) * (0.22 + rng() * 0.64);
            const h = r * (0.16 + rng() * 0.14);
            const w = h * (0.5 + rng() * 0.22);

            ctx.save();
            ctx.globalCompositeOperation = 'multiply';
            ctx.fillStyle = 'rgba(0,0,0,0.10)';
            ctx.beginPath();
            ctx.ellipse(x + w * 0.12, y + h * 0.34, w * 0.35, h * 0.18, 0, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();

            const trunkH = h * 0.25;
            const trunkW = w * 0.18;
            ctx.fillStyle = 'rgba(30,20,10,0.28)';
            ctx.fillRect(x - trunkW / 2, y + h * 0.1, trunkW, trunkH);

            ctx.beginPath();
            ctx.moveTo(x, y - h * 0.62);
            ctx.lineTo(x - w, y + h * 0.06);
            ctx.lineTo(x + w, y + h * 0.06);
            ctx.closePath();
            ctx.stroke();

            ctx.save();
            ctx.globalCompositeOperation = 'screen';
            ctx.fillStyle = 'rgba(255,255,255,0.08)';
            ctx.beginPath();
            ctx.moveTo(x, y - h * 0.6);
            ctx.lineTo(x - w * 0.7, y + h * 0.0);
            ctx.lineTo(x, y + h * 0.02);
            ctx.closePath();
            ctx.fill();
            ctx.restore();
        }

        stipple(ctx, rng, bounds, 10 + Math.floor(rng() * 14), 0.05, zoom);
    });
}

function drawWater({ ctx, tile, center, verts, zoom, neighbors }: DrawArgs) {
    const r = hexRadius(center, verts);
    const rng = mulberry32(tileSeed(tile, fnv1a('water')));
    const bounds = getHexBounds(verts);
    const edges = edgeDirs(verts, center);

    const landEdge = new Array(6).fill(false);
    if (neighbors?.length) {
        for (const nb of neighbors) {
            const index = edgeIndexToward(verts, center, nb.center);
            if (!isWaterLike(nb.tile)) landEdge[index] = true;
        }
    }

    clipHex(ctx, verts, () => {
        ctx.lineWidth = 1 / zoom;
        ctx.strokeStyle = 'rgba(255,255,255,0.16)';
        const rowGap = r * 0.22;
        const amp = r * 0.055;
        const startY = bounds.top + r * (0.22 + rng() * 0.1);

        for (let y = startY; y < bounds.bottom - r * 0.12; y += rowGap) {
            let x = bounds.left - r * 0.2;
            const endX = bounds.right + r * 0.2;
            const seg = r * 0.33;
            ctx.beginPath();
            ctx.moveTo(x, y);
            while (x < endX) {
                const midX = x + seg / 2;
                const nextX = x + seg;
                ctx.quadraticCurveTo(midX, y - amp, nextX, y);
                x = nextX;
            }
            ctx.stroke();
        }

        for (let i = 0; i < 6; i += 1) {
            if (!landEdge[i]) continue;

            const e = edges[i];
            const inward = { x: -e.out.x, y: -e.out.y };

            ctx.save();
            ctx.globalCompositeOperation = 'multiply';
            ctx.strokeStyle = 'rgba(0,0,0,0.10)';
            ctx.lineWidth = (5.0 + rng() * 2.0) / zoom;
            ctx.beginPath();
            ctx.moveTo(e.a.x + inward.x * (2.0 / zoom), e.a.y + inward.y * (2.0 / zoom));
            ctx.lineTo(e.b.x + inward.x * (2.0 / zoom), e.b.y + inward.y * (2.0 / zoom));
            ctx.stroke();
            ctx.restore();

            drawFoamAlongEdge(ctx, e.a, e.b, inward, rng, zoom);
        }

        ctx.save();
        ctx.globalCompositeOperation = 'screen';
        ctx.fillStyle = 'rgba(255,255,255,0.12)';
        const dots = 5 + Math.floor(rng() * 7);
        for (let i = 0; i < dots; i += 1) {
            const x = bounds.left + (bounds.right - bounds.left) * (0.2 + rng() * 0.6);
            const y = bounds.top + (bounds.bottom - bounds.top) * (0.24 + rng() * 0.55);
            const rad = (1.1 + rng() * 1.7) / zoom;
            ctx.beginPath();
            ctx.arc(x, y, rad, 0, Math.PI * 2);
            ctx.fill();
        }
        ctx.restore();
    });
}

function drawPlains({ ctx, tile, center, verts, zoom }: DrawArgs) {
    const r = hexRadius(center, verts);
    const rng = mulberry32(tileSeed(tile, fnv1a('plains')));
    const bounds = getHexBounds(verts);

    clipHex(ctx, verts, () => {
        hatch(ctx, bounds, -0.85, (9.0 + rng() * 3.0) / zoom, 0.035, zoom);

        ctx.lineWidth = 1 / zoom;
        ctx.strokeStyle = 'rgba(0,0,0,0.10)';
        const bands = 2 + Math.floor(rng() * 3);
        for (let i = 0; i < bands; i += 1) {
            const y = bounds.top + (bounds.bottom - bounds.top) * (0.3 + rng() * 0.45);
            const amp = r * (0.025 + rng() * 0.02);
            const seg = r * (0.38 + rng() * 0.15);

            let x = bounds.left - r * 0.25;
            const endX = bounds.right + r * 0.25;

            ctx.beginPath();
            ctx.moveTo(x, y);
            while (x < endX) {
                const midX = x + seg / 2;
                const nextX = x + seg;
                ctx.quadraticCurveTo(midX, y + (rng() > 0.5 ? -amp : amp), nextX, y);
                x = nextX;
            }
            ctx.stroke();
        }

        stipple(ctx, rng, bounds, 8 + Math.floor(rng() * 10), 0.035, zoom);
    });
}

function drawHills({ ctx, tile, center, verts, zoom }: DrawArgs) {
    const r = hexRadius(center, verts);
    const rng = mulberry32(tileSeed(tile, fnv1a('hills')));
    const bounds = getHexBounds(verts);

    clipHex(ctx, verts, () => {
        ctx.lineWidth = 1 / zoom;
        ctx.strokeStyle = 'rgba(0,0,0,0.18)';

        const lines = 3 + Math.floor(rng() * 4);
        for (let i = 0; i < lines; i += 1) {
            const t = (i + 1) / (lines + 1);
            const y =
                bounds.top +
                (bounds.bottom - bounds.top) * (0.22 + 0.62 * t) +
                (rng() - 0.5) * r * 0.05;
            const amp = r * (0.035 + rng() * 0.03) * (0.85 - 0.45 * t);
            const seg = r * (0.3 + rng() * 0.16);

            let x = bounds.left - r * 0.22;
            const endX = bounds.right + r * 0.22;

            ctx.beginPath();
            ctx.moveTo(x, y);
            while (x < endX) {
                const midX = x + seg / 2;
                const nextX = x + seg;
                ctx.quadraticCurveTo(midX, y + (rng() > 0.5 ? -amp : amp), nextX, y);
                x = nextX;
            }
            ctx.stroke();
        }

        hatch(ctx, bounds, -0.95, (7.5 + rng() * 2.5) / zoom, 0.06, zoom);
    });
}

function drawWetlands({ ctx, tile, center, verts, zoom }: DrawArgs) {
    const r = hexRadius(center, verts);
    const rng = mulberry32(tileSeed(tile, fnv1a('wetlands')));
    const bounds = getHexBounds(verts);

    clipHex(ctx, verts, () => {
        hatch(ctx, bounds, -0.95, (7.0 + rng() * 3.0) / zoom, 0.07, zoom);

        const puddles = 2 + Math.floor(rng() * 3);
        for (let i = 0; i < puddles; i += 1) {
            const x = bounds.left + (bounds.right - bounds.left) * (0.25 + rng() * 0.5);
            const y = bounds.top + (bounds.bottom - bounds.top) * (0.28 + rng() * 0.5);
            const rx = r * (0.1 + rng() * 0.1);
            const ry = r * (0.07 + rng() * 0.09);

            ctx.save();
            ctx.globalCompositeOperation = 'multiply';
            ctx.fillStyle = 'rgba(0,0,0,0.18)';
            ctx.beginPath();
            ctx.ellipse(x, y, rx, ry, rng() * 0.8, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();

            ctx.save();
            ctx.globalCompositeOperation = 'screen';
            ctx.fillStyle = 'rgba(255,255,255,0.08)';
            ctx.beginPath();
            ctx.ellipse(x - rx * 0.25, y - ry * 0.15, rx * 0.45, ry * 0.35, 0, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();
        }

        ctx.strokeStyle = 'rgba(0,0,0,0.24)';
        ctx.lineWidth = 1 / zoom;
        const reeds = 8 + Math.floor(rng() * 10);
        for (let i = 0; i < reeds; i += 1) {
            const x = bounds.left + (bounds.right - bounds.left) * rng();
            const y = bounds.top + (bounds.bottom - bounds.top) * (0.35 + rng() * 0.55);
            const h = r * (0.1 + rng() * 0.12);
            ctx.beginPath();
            ctx.moveTo(x, y);
            ctx.quadraticCurveTo(
                x + (rng() - 0.5) * r * 0.08,
                y - h * 0.6,
                x + (rng() - 0.5) * r * 0.1,
                y - h,
            );
            ctx.stroke();
        }
    });
}

function drawCoast({ ctx, tile, center, verts, zoom, neighbors }: DrawArgs) {
    const rng = mulberry32(tileSeed(tile, fnv1a('coast')));
    const bounds = getHexBounds(verts);
    const edges = edgeDirs(verts, center);

    const waterEdge = new Array(6).fill(false);
    if (neighbors?.length) {
        for (const nb of neighbors) {
            const index = edgeIndexToward(verts, center, nb.center);
            if (isWaterLike(nb.tile)) waterEdge[index] = true;
        }
    }

    clipHex(ctx, verts, () => {
        hatch(ctx, bounds, -0.85, (10.0 + rng() * 3.0) / zoom, 0.03, zoom);
        stipple(ctx, rng, bounds, 10 + Math.floor(rng() * 14), 0.045, zoom);

        for (let i = 0; i < 6; i += 1) {
            if (!waterEdge[i]) continue;

            const e = edges[i];
            const inward = { x: -e.out.x, y: -e.out.y };

            ctx.save();
            ctx.globalCompositeOperation = 'screen';
            ctx.strokeStyle = 'rgba(255,255,255,0.10)';
            ctx.lineWidth = (6.5 + rng() * 2.5) / zoom;
            ctx.beginPath();
            ctx.moveTo(e.a.x + inward.x * (1.2 / zoom), e.a.y + inward.y * (1.2 / zoom));
            ctx.lineTo(e.b.x + inward.x * (1.2 / zoom), e.b.y + inward.y * (1.2 / zoom));
            ctx.stroke();
            ctx.restore();

            drawFoamAlongEdge(ctx, e.a, e.b, inward, rng, zoom);
        }

        ctx.lineWidth = 1.05 / zoom;
        ctx.strokeStyle = 'rgba(0,0,0,0.22)';
        for (let i = 0; i < 6; i += 1) {
            if (!waterEdge[i]) continue;
            const e = edges[i];
            ctx.beginPath();
            ctx.moveTo(e.a.x, e.a.y);
            ctx.lineTo(e.b.x, e.b.y);
            ctx.stroke();
        }
    });
}
